import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, AlertTriangle, CheckCircle, Search, Calendar, User } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { creditosAPI } from '../api/client';

const CreditoPage = () => {
  const [creditos, setCreditos] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');
  const [loading, setLoading] = useState(false);
  const { formatCurrency } = useConfig();

  useEffect(() => {
    loadCreditos();
  }, []);

  const loadCreditos = async () => {
    setLoading(true);
    try {
      const response = await creditosAPI.getAll();
      if (response.data.success) {
        const creditosData = response.data.data.map(c => ({
          ...c,
          total: parseFloat(c.monto_total),
          saldo: parseFloat(c.saldo_pendiente),
          pagado: parseFloat(c.monto_pagado)
        }));
        setCreditos(creditosData);
      }
    } catch (error) {
      console.error('Error al cargar créditos:', error);
      alert('Error al cargar créditos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const registrarPago = async (creditoId) => {
    const credito = creditos.find(c => c.id === creditoId);
    if (!credito) return;

    const monto = prompt(`Ingrese el monto a pagar (Saldo: ${formatCurrency(credito.saldo)}):`);
    if (!monto) return;

    const montoNumerico = parseFloat(monto);
    if (isNaN(montoNumerico) || montoNumerico <= 0) {
      alert('Monto inválido');
      return;
    }

    if (montoNumerico > credito.saldo) {
      alert('El monto no puede ser mayor al saldo');
      return;
    }

    try {
      const response = await creditosAPI.registrarPago(creditoId, montoNumerico);
      if (response.data.success) {
        alert('Pago registrado exitosamente');
        loadCreditos(); // Recargar créditos
      }
    } catch (error) {
      console.error('Error al registrar pago:', error);
      alert('Error al registrar pago: ' + (error.response?.data?.message || error.message));
    }
  };

  const creditosFiltrados = creditos.filter(c => {
    const matchFiltro = c.folio_venta.toLowerCase().includes(filtro.toLowerCase()) ||
                        c.cliente_nombre.toLowerCase().includes(filtro.toLowerCase());
    const matchEstado = estadoFiltro === 'todos' || c.estado === estadoFiltro;
    return matchFiltro && matchEstado;
  });

  const stats = {
    total: creditos.reduce((sum, c) => sum + c.total, 0),
    porCobrar: creditos.filter(c => c.estado !== 'pagado').reduce((sum, c) => sum + c.saldo, 0),
    vencidos: creditos.filter(c => c.estado === 'vencido').reduce((sum, c) => sum + c.saldo, 0),
    pagados: creditos.filter(c => c.estado === 'pagado').length
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      activo: 'bg-blue-100 text-blue-800',
      vencido: 'bg-red-100 text-red-800',
      pagado: 'bg-green-100 text-green-800'
    };
    return badges[estado] || badges.activo;
  };

  const getDiasVencidos = (fechaVencimiento) => {
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diferencia = Math.floor((hoy - vencimiento) / (1000 * 60 * 60 * 24));
    return diferencia > 0 ? diferencia : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Crédito y Cuentas por Cobrar</h1>
          <p className="text-gray-600 mt-1">Gestión de ventas a crédito y cobranza</p>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Crédito Total</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total)}</p>
            </div>
            <CreditCard className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Por Cobrar</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.porCobrar)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vencidos</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(stats.vencidos)}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pagados</p>
              <p className="text-2xl font-bold text-green-600">{stats.pagados}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar por folio o cliente..."
              className="input-field pl-10"
            />
          </div>

          <select
            value={estadoFiltro}
            onChange={(e) => setEstadoFiltro(e.target.value)}
            className="input-field"
          >
            <option value="todos">Todos los estados</option>
            <option value="activo">Activos</option>
            <option value="vencido">Vencidos</option>
            <option value="pagado">Pagados</option>
          </select>
        </div>
      </div>

      {/* Tabla de créditos */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Folio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Venta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vencimiento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pagado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Saldo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {creditosFiltrados.map((credito) => {
                const diasVencidos = getDiasVencidos(credito.fecha_vencimiento);
                return (
                  <tr key={credito.id} className={`hover:bg-gray-50 ${credito.estado === 'vencido' ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{credito.folio_venta}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <User className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-900">{credito.cliente_nombre}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        {new Date(credito.fecha_venta).toLocaleDateString('es-MX')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <span className={diasVencidos > 0 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                          {new Date(credito.fecha_vencimiento).toLocaleDateString('es-MX')}
                        </span>
                      </div>
                      {diasVencidos > 0 && (
                        <span className="text-xs text-red-600">({diasVencidos} días vencido)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(credito.total)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                      {formatCurrency(credito.pagado)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-orange-600">
                      {formatCurrency(credito.saldo)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEstadoBadge(credito.estado)}`}>
                        {credito.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {credito.estado !== 'pagado' && (
                        <button
                          onClick={() => registrarPago(credito.id)}
                          className="btn-primary text-sm py-1 px-3"
                        >
                          Registrar Pago
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {creditosFiltrados.length === 0 && (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No se encontraron créditos</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditoPage;
